import * as React from 'react';

import { type ErrorLike, type RefetchQueriesInclude } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Avatar, CardHeader, Stack, Typography } from '@mui/material';
import { Trash2Icon, UserIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { GET_PENDING_ACTIVITIES } from '@modules/_shared/components/feed/utils/activityQueries';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useLocale } from '@shared/hooks/locale';
import { fromGlobalId } from '@shared/utils/global-id';

type RefetchQueries = Extract<RefetchQueriesInclude, readonly unknown[]>;

const DELETE_ACTIVITY = gql(/* GraphQL */ `
  mutation FeedDeleteDialogDeleteActivity($id: uuid!) {
    delete_activities_by_pk(id: $id) {
      id
    }
  }
`);

type Props = {
  activity?: {
    id: string;
  };
  activityId?: string;
  title: React.ReactNode;
  createdAt: null | string;
  authorInitials: string | null;
  open: boolean;
  invisible?: boolean;
  extraRefetchQueries?: RefetchQueries;
  onClose: () => void;
  onDelete: () => void;
};

export const FeedDeleteDialog = (props: Props) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = React.useState<ErrorLike | null>(null);
  const [deleteActivity, { loading }] = useMutation(DELETE_ACTIVITY, {
    refetchQueries: [
      GET_PENDING_ACTIVITIES,
      ...(props.extraRefetchQueries ?? []),
    ],
    awaitRefetchQueries: true,
    onError: setErrorLike,
  });

  const handleConfirm = async () => {
    const id =
      props.activityId ??
      (props.activity ? fromGlobalId(props.activity.id) : null);
    if (!id) {
      return;
    }

    setErrorLike(null);

    const result = await deleteActivity({
      variables: { id },
    });

    if (result.data?.delete_activities_by_pk) {
      emitFeedRefresh();
      props.onDelete();
    }
  };

  return (
    <>
      <SlideDialog
        open={props.open}
        onClose={() => {
          if (loading) {
            return;
          }
          props.onClose();
        }}
        title={t('Are you sure you want to delete this activity?')}
        onSave={handleConfirm}
        loading={loading}
        saveColor="error"
        saveText={t('Delete')}
        saveIcon={<Trash2Icon />}
        cancelText={t('Cancel')}
        dialogProps={{
          slotProps: { backdrop: { invisible: !!props.invisible } },
        }}
      >
        <Stack spacing={2} sx={{ p: 3 }}>
          <CardHeader
            avatar={
              <Avatar>{props.authorInitials || <UserIcon size={20} />}</Avatar>
            }
            title={props.title}
            subheader={<TimeAgo dateString={props.createdAt} />}
            css={{
              padding: 0,
              '.MuiCardHeader-avatar': {
                marginRight: 12,
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t(
              'This action cannot be undone. To confirm, please click Delete.',
            )}
          </Typography>
        </Stack>
      </SlideDialog>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
